/* A conservative one-dimensional shallow-water surface, in CSS-pixel units.
   Gravity-driven face velocities move volume between columns; a drain removes
   measured volume. This is a stylized 2D cross-section, not a 3D fluid engine. */
(function (root) {
  'use strict';
  class NyeFluid {
    constructor(width, height, columns = 80) {
      this.n = columns; this.width = width; this.height = height;
      this.dx = width / columns; this.h = new Float64Array(columns);
      this.u = new Float64Array(columns + 1); this.drained = 0;
      this.pigment = Array.from({length:3}, () => new Float64Array(columns));
    }
    get volume() { return this.h.reduce((sum, v) => sum + v, 0) * this.dx; }
    get fraction() { return this.volume / (this.width * this.height); }
    pigmentTotals() { return this.pigment.map(a => a.reduce((sum,v) => sum+v,0)*this.dx); }
    colorAt(index) {
      const k=Math.max(0,Math.min(this.n-1,Math.round(index)));
      return this.h[k]<1e-9 ? [198,128,173] : this.pigment.map(a=>Math.max(0,Math.min(255,a[k]/this.h[k])));
    }
    scale(ratio) {
      this.h.forEach((v,i)=>{this.h[i]=v*ratio;});
      this.pigment.forEach(a=>a.forEach((v,i)=>{a[i]=v*ratio;}));
    }
    resize(width, height) {
      const scale = this.width / width;
      this.scale(scale);
      this.width = width; this.height = height; this.dx = width / this.n;
      const overflow = Math.max(0, this.volume - width * height);
      if (overflow > 0) {
        const ratio = width * height / this.volume;
        this.scale(ratio);
        this.drained += overflow;
      }
    }
    add(x, area, color=[198,128,173]) {
      const accepted = Math.max(0, Math.min(area, this.width * this.height - this.volume));
      const index = Math.max(0, Math.min(this.n - 1, Math.floor(x / this.dx)));
      const weights = [1, 3, 5, 3, 1]; let sum = 0;
      weights.forEach((w, j) => { if (index + j - 2 >= 0 && index + j - 2 < this.n) sum += w; });
      weights.forEach((w, j) => { const k = index + j - 2; if (k >= 0 && k < this.n) { const depth=accepted*w/sum/this.dx; this.h[k]+=depth; this.pigment.forEach((a,c)=>{a[k]+=depth*color[c];}); } });
      return accepted;
    }
    splash(x, impulse) {
      const k = Math.max(1, Math.min(this.n - 1, Math.round(x / this.dx)));
      this.u[k - 1] -= impulse; this.u[k] += impulse;
      this.u[0] = this.u[this.n] = 0;
    }
    step(dt, drain = false) {
      const maxDepth = Math.max(1, ...this.h);
      const stable = Math.min(1 / 60, 0.3 * this.dx / (Math.sqrt(140 * maxDepth) + 320));
      const steps = Math.max(1, Math.ceil(Math.min(dt, 0.05) / stable));
      const t = Math.min(dt, 0.05) / steps;
      let removed = 0;
      for (let s = 0; s < steps; s++) {
        const delta = new Float64Array(this.n);
        const dyes = Array.from({length:3},()=>new Float64Array(this.n));
        for (let i = 1; i < this.n; i++) {
          const velocity = (this.u[i] + 140 * (this.h[i - 1] - this.h[i]) / this.dx * t) * Math.exp(-3.5 * t);
          this.u[i] = Math.max(-320, Math.min(320, velocity));
          let flow = this.u[i] * (this.h[i - 1] + this.h[i]) * 0.5 * t / this.dx;
          flow = Math.max(-this.h[i] * 0.44, Math.min(this.h[i - 1] * 0.44, flow));
          delta[i - 1] -= flow; delta[i] += flow;
          const donor=flow>=0 ? i-1 : i;
          if(this.h[donor]>1e-12)this.pigment.forEach((a,c)=>{
            const dye=flow*a[donor]/this.h[donor]; dyes[c][i-1]-=dye; dyes[c][i]+=dye;
          });
        }
        this.h.forEach((v, i) => { this.h[i] = Math.max(0, v + delta[i]); });
        this.pigment.forEach((a,c)=>a.forEach((v,i)=>{a[i]=Math.max(0,v+dyes[c][i]);}));
        for(let i=1;i<this.n;i++) {
          if(this.h[i]<.01||this.h[i-1]<.01)continue;
          const exchange=Math.min(this.h[i],this.h[i-1])*.16*t;
          this.pigment.forEach(a=>{const d=(a[i-1]/this.h[i-1]-a[i]/this.h[i])*exchange;a[i-1]-=d;a[i]+=d;});
        }
        if (drain) {
          // A bottom outlet with head-dependent flow. Broad catchment keeps it
          // usable even on a tall mobile page; the outlet animates in the UI.
          const mean = this.volume / this.width;
          const wanted = this.width * Math.max(18, Math.sqrt(2 * 140 * mean) * 0.8) * t;
          const take = Math.min(this.volume, wanted);
          if (take > 0) {
            const ratio = 1 - take / this.volume;
            this.scale(ratio);
            removed += take;
          }
        }
      }
      this.drained += removed;
      return removed;
    }
    clear() { this.h.fill(0); this.u.fill(0); this.pigment.forEach(a=>a.fill(0)); this.drained = 0; }
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = NyeFluid;
  else root.NyeFluid = NyeFluid;
})(typeof window !== 'undefined' ? window : globalThis);
